// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp, ref, configureCompat } from 'vue'
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar'

import App from './AppTmpl'
import router from './router'
import ElementPlus, {
  ElDropdown,
  ElPopperTrigger,
  ElScrollbar,
  ElDropdownItem,
  ElPopperContent,
  ElUpload,
  ElInput,
  ElSelect,
  ElPopper,
  ElTooltip,
  ElOption,
  ElConfigProvider
} from 'element-plus'
import i18n from '@/i18n/i18ns'
import { createPinia } from 'pinia'
import dayjs from 'dayjs';
import 'dayjs/locale/en';

import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCirclePlay, faFaceSmile, faCopy, faCircleXmark, faCheckCircle } from '@fortawesome/free-regular-svg-icons'
import { faExpand, faPlay, faStop, faXmark, faCircleExclamation, faChevronDown, faSearch, faAngleUp, faAngleDown, faCaretUp, faCaretDown, faCircleNotch, faExternalLink, faQuestion } from '@fortawesome/free-solid-svg-icons'

import * as ElementPlusIconsVue from '@element-plus/icons-vue'

import AxiosPlugin from '@/plugins/axios.js'
import AuthPlugin from '@/plugins/auth.js'
import IconifyPlugin from '@/plugins/iconify.js'
import APIError from '@/globals/api-error.js'
import CurrentUser from '@/globals/current-user-info.js'
import CustomPopup from '@/components/common/custom-popup/custom-popup-tmpl'
import Banner from '@/components/common/banner/banner-tmpl'
import CustomNotification from '@/components/common/custom-notification/custom-notification.vue'
import DataStore from '@/globals/data-store.js'
import FileUploader from '@/components/common/file-uploader/file-uploader.vue'
import AppBackground from '@/components/common/app-background/app-background-tmpl'
import Navigation from '@/components/common/navigation/navigation-tmpl'
import SideNavigation from '@/components/common/side-navigation/side-navigation-tmpl'
import MainLoader from '@/components/common/main-loader/main-loader-tmpl'
import DebugPanel from '@/components/common/debug-panel/debug-panel-tmpl'
import Loader from '@/components/common/loader/loader-tmpl'
import LanguageSelector from '@/components/common/language-selector/language-selector-tmpl'
import FullScreenVideo from '@/components/common/full-screen-video/full-screen-video-tmpl'
import PlayerPreviewPopup from '@/components/common/player-preview-popup/player-preview-popup-tmpl'
import Utils, { Dependencies } from '@/globals/utils.js'

import DialogPopup from '@/components/common/dialog-popup/dialog-popup-tmpl'
import DialogPopupPage from '@/components/common/dialog-popup/dialog-popup-page/dialog-popup-page-tmpl'
import Alert from './components/common/alert/alert-tmpl.vue'
import AlertBanner from './components/common/alert-banner/alert-banner-tmpl.vue'

import FormInput from './components/common/form/form-input-tmpl.vue'
import FormSelect from './components/common/form/form-select-tmpl.vue'
import Paginator from './components/common/paginator/paginator.vue'

import BaseIconSvg from '@base-shared/components/IconSvg.vue'
import BaseProductSelectNav from '@base-shared/components/ProductSelectNav.vue'
import BaseSideMenu from '@base-shared/components/SideMenu.vue'
import BaseLiveChatButton from '@base-shared/components/LiveChatButton.vue'
import BaseDataTable from '@base-shared/components/DataTable.vue';
import BaseEmptyState from '@base-shared/components/EmptyState.vue'
import BaseNotificationAlertsItem from '@base-shared/components/NotificationAlertsItem.vue'
import BaseCommonDialogModal from '@base-shared/components/CommonDialogModal.vue'

import AryaPreAuthComponent from './AryaPreAuthComponent.vue'

import 'element-plus/theme-chalk/index.css'
import 'vue3-perfect-scrollbar/style.css'
import { FwbButton, FwbButtonGroup, FwbInput, FwbCheckbox, FwbRadio, FwbDropdown, FwbTooltip } from 'flowbite-vue'

import {
  store
} from './vuex/store'
import FiltersRepository from './globals/filtersRepository'
import PollingManager from '@base-shared/utils/polling-manager'
import { DependencyManager } from '@base-shared/utils/dependency-manager'
import { handleProductEnableForExternalSignIn } from '@/globals/external-provider-auth-utils.js'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
/**
 * FIX for vuedraggable error with rendering
 * TODO: remove once done with migration build with vue comapat
 * Source: https://github.com/SortableJS/vue.draggable.next/issues/87
 */
configureCompat({
  COMPONENT_V_MODEL: false,
  RENDER_FUNCTION: false
})

const app = createApp(App)

app.use(AxiosPlugin, {
  GLOBAL_STAGE: CONFIG.GLOBAL_STAGE,
  API_BASE_URL: CONFIG.API_BASE_URL,
  API_BASE_URLS: CONFIG.API_BASE_URLS,
  API_FEEDS_URL: CONFIG.API_FEEDS_URL,
  API_FEEDS_URLS: CONFIG.API_FEEDS_URLS,
  router,
  Dependencies
});

/**
 * Get the Axios instance from the plugin and set it for use in
 * useApi composable @see {@link @base-shared/composables/use-api}
 */
DependencyManager.set({
  api: {
    getAxios: () => Dependencies.inject('$baseApi')
  },
  auth: {
    onAuthEvent: async (eventName) => {
      if (eventName === 'signedIn') {
        // On signedIn event, perform additional actions especially for
        // external IDP logins to enable product on newly created user and
        // account.
        const session = await useCognitoAuthStore().fetchAndStoreAuthSession();
        await handleProductEnableForExternalSignIn(session.tokens?.idToken?.payload);
      }
    }
  }
});

// Register Iconify plugin for use in Vue components
app.use(IconifyPlugin);

// Set weekstart based on user's locale
dayjs.locale('en', {
  ...dayjs.Ls.en,
  weekStart: Utils.getCalendarWeekStart(navigator.language)
}, null, true);

/**
 * Global signedInUser data
 */
const signedInUserDetails = ref(null)
const signedInUserInfo = ref(null)
const signedInUserAccounts = ref([])

/**
 * Instantiate CurrentUser dependencies
 */
CurrentUser.setSignedInUserDetails(signedInUserDetails)

/**
 * Instantiate DataStore dependencies
 */
DataStore.setCurrentUserInstance(CurrentUser)

/**
 * Initialize FiltersRepository instance and dependency
 */
FiltersRepository.init(DataStore)

/**
 * Register global dependency injections
 */
app.provide('$currentUser', CurrentUser)
app.provide('$utils', Utils)
app.provide('$dataStore', DataStore)
app.provide('$apiError', APIError)
app.provide('$filtersRepository', FiltersRepository)
app.provide('$pollingManager', PollingManager)
app.provide('$routerInstance', router)

// Vue-independent dependencies
Dependencies.provide('$currentUser', CurrentUser)
Dependencies.provide('$routerInstance', router)
Dependencies.provide('$dataStore', DataStore)

/**
 * Register global signedInUser data
 *
 * Take note of injecting these for some places might
 * require accessing them with `.value` and might not
 * be unwrapped automatically.
 * (e.g. in vue templates, composition/options api)
 */
app.provide('$signedInUserDetails', signedInUserDetails)
app.provide('$signedInUserAccounts', signedInUserAccounts)
app.provide('$signedInUserInfo', signedInUserInfo)

// Compat configuration for ElementPlus components
ElDropdown.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElDropdown.components.ElRovingFocusGroup.components.ElRovingFocusGroupImpl.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElDropdown.DropdownItem.components.ElDropdownItemImpl.compatConfig = { MODE: 3 } // Warning: ATTR_FALSE_VALUE
ElDropdownItem.compatConfig = { MODE: 3 } // Warning: INSTANCE_ATTRS_CLASS_STYLE
ElPopper.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElPopperTrigger.compatConfig = { MODE: 3 } // Warning: INSTANCE_ATTRS_CLASS_STYLE, WATCH_ARRAY
ElPopperContent.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY, ATTR_FALSE_VALUE
ElScrollbar.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElUpload.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElInput.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY, INSTANCE_ATTRS_CLASS_STYLE
ElSelect.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElTooltip.compatConfig = { MODE: 3 } // Warning: WATCH_ARRAY
ElOption.compatConfig = { MODE: 3 } // Warning: ATTR_FALSE_VALUE

// Compat configuration for Flowbite Vue components
FwbButton.compatConfig = { MODE: 3 }
FwbButtonGroup.compatConfig = { MODE: 3 }
FwbInput.compatConfig = { MODE: 3 }
FwbCheckbox.compatConfig = { MODE: 3 }
FwbRadio.compatConfig = { MODE: 3 }
FwbDropdown.compatConfig = { MODE: 3 }
FwbTooltip.compatConfig = { MODE: 3 }

const globalProperties = app.config.globalProperties
export { globalProperties }

/* add icons to the library */
FontAwesomeLibrary.add(
  faAngleUp,
  faCirclePlay,
  faAngleDown,
  faExpand,
  faPlay,
  faStop,
  faXmark,
  faFaceSmile,
  faCopy,
  faCircleExclamation,
  faChevronDown,
  faSearch,
  faCircleXmark,
  faCheckCircle,
  faCaretUp,
  faCaretDown,
  faCircleNotch,
  faExternalLink,
  faQuestion
)
app.component('font-awesome-icon', FontAwesomeIcon)

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

// $.extend(JQueryUI)
// $.extend(JQueryUI2)

/**
  Register global components and Create Vue instance
*/
app.component('app-background', AppBackground)
app.component('custom-popup', CustomPopup)
app.component('loader', Loader)
app.component('language-selector', LanguageSelector)
app.component('main-loader', MainLoader)
app.component('navigation', Navigation)
app.component('side-navigation', SideNavigation)
app.component('custom-notification', CustomNotification)
app.component('file-uploader', FileUploader)
app.component('full-screen-video', FullScreenVideo)
app.component('player-preview-popup', PlayerPreviewPopup)
app.component('banner', Banner)
app.component('debug-panel', DebugPanel)
app.component('DialogPopup', DialogPopup)
app.component('DialogPopupPage', DialogPopupPage)
app.component('Alert', Alert)
app.component('AlertBanner', AlertBanner)
app.component('FormInput', FormInput)
app.component('FormSelect', FormSelect)
app.component('Paginator', Paginator)
app.component('ElConfigProvider', ElConfigProvider)
app.component('BaseIconSvg', BaseIconSvg)
app.component('BaseProductSelectNav', BaseProductSelectNav)
app.component('BaseSideMenu', BaseSideMenu)
app.component('BaseLiveChatButton', BaseLiveChatButton)
app.component('BaseDataTable', BaseDataTable)
app.component('BaseEmptyState', BaseEmptyState)
app.component('BaseNotificationAlertsItem', BaseNotificationAlertsItem)
app.component('BaseCommonDialogModal', BaseCommonDialogModal)
app.component('AryaPreAuthComponent', AryaPreAuthComponent)

const pinia = createPinia()
app.use(pinia)
// Important: Register AuthPlugin after pinia since
// AuthPlugin now uses Pinia store for user and group data
// instead of EventBus and callbacks.
app.use(AuthPlugin, {
  signedInUserDetails,
  signedInUserAccounts,
  signedInUserInfo,
  router
})

app.use(ElementPlus)
app.use(PerfectScrollbarPlugin)
app.use(i18n)
app.use(store)
app.use(router)
app.mount('#app')
